import { Dispatch } from 'react';
import { RootState } from 'store/configureStore';
import {
  cachedGetClubsByClubName,
  cachedGetFirmsByFCA,
  cachedGetNetworksByNetworkName,
  registerAccountToExistingFirm,
  registerFirmWithManager,
} from '../api';
import { Club, Network } from '../api/types';
import { ACTION_TYPES, State } from './types';
import { Firm } from 'screens/BrokerPortal/screens/Registration/api';
import { Form as SearchFirmForm } from 'screens/BrokerPortal/screens/Registration/components/SearchFirmsStep/types';
import { Form as RegisterFirmForm } from 'screens/BrokerPortal/screens/Registration/components/RegisterFirmStep/types';
import { Form as DueDiligenceForm } from 'screens/BrokerPortal/screens/Registration/components/DueDiligenceStep/types';
import { Nullable } from 'utils/types';
import registerFirmWithManagerMapper from '../mapper/registerFirmWithManagerMapper';
import registerAccountToExistingFirmMapper from 'screens/BrokerPortal/screens/Registration/mapper/registerAccountToExistingFirmMapper';
import { Step } from 'screens/BrokerPortal/screens/Registration/steps';

export const setSearchFirmsForm = (data: SearchFirmForm) => ({
  type: ACTION_TYPES.SET_SEARCH_FIRMS_FORM,
  payload: data,
});

export const setFirmsItems = (data: Firm[]) => ({
  type: ACTION_TYPES.SET_SEARCH_FIRMS_FIRMS_ITEMS,
  payload: data,
});

export const setFirmsOptionsLoading = (data: boolean) => ({
  type: ACTION_TYPES.SET_SEARCH_FIRMS_FIRMS_ITEMS_LOADING,
  payload: data,
});

export const setDueDiligenceForm = (data: DueDiligenceForm) => ({
  type: ACTION_TYPES.SET_DUE_DILIGENCE_FORM,
  payload: data,
});

export const setRegisterFirmForm = (data: RegisterFirmForm) => ({
  type: ACTION_TYPES.SET_REGISTER_FIRM_FORM,
  payload: data,
});

export const setPersonalDetailsForm = (data: any) => ({
  type: ACTION_TYPES.SET_PERSONAL_DETAILS_FORM,
  payload: data,
});

export const setNetworkItems = (data: Network[]) => ({
  type: ACTION_TYPES.SET_NETWORK_ITEMS,
  payload: data,
});

export const setNetworkOptionsLoading = (data: boolean) => ({
  type: ACTION_TYPES.SET_NETWORK_ITEMS_LOADING,
  payload: data,
});

export const setClubItems = (data: Club[]) => ({
  type: ACTION_TYPES.SET_CLUB_ITEMS,
  payload: data,
});

export const setClubOptionsLoading = (data: boolean) => ({
  type: ACTION_TYPES.SET_CLUB_ITEMS_LOADING,
  payload: data,
});

export const setAddAddressManually = (data: boolean) => ({
  type: ACTION_TYPES.SET_ADD_ADDRESS_MANUALLY,
  payload: data,
});

export const setContextFirm = (firm: Nullable<Firm>) => ({
  type: ACTION_TYPES.SET_CONTEXT_FIRM,
  payload: firm,
});

export const setRegisterLoading = (loading: boolean) => ({
  type: ACTION_TYPES.SET_REGISTER_LOADING,
  payload: loading,
});

export const setCurrentStep = (step: Step) => ({
  type: ACTION_TYPES.SET_CURRENT_STEP,
  payload: step,
});

export const clearRegistration = () => ({
  type: ACTION_TYPES.CLEAR_REGISTRATION,
  payload: undefined,
});

export const loadFirmsByFcaNumber = (fcaNumber: string) => {
  return async (dispatch: Dispatch<any>, getState: RootState) => {
    dispatch(setFirmsOptionsLoading(true));
    try {
      const firms = await cachedGetFirmsByFCA(fcaNumber);
      const sortByFcaNumber = firms.sort((a, b) => a.fcaNumber.localeCompare(b.fcaNumber));
      dispatch(setFirmsItems(sortByFcaNumber));
    } catch (e) {
      console.log(e);
    }
    dispatch(setFirmsOptionsLoading(false));
  };
};

export const loadNetworksByNetworkName = (networkName: string) => {
  return async (dispatch: Dispatch<any>, getState: RootState) => {
    dispatch(setNetworkOptionsLoading(true));
    try {
      const networks = await cachedGetNetworksByNetworkName(networkName);
      const sortAlphabetically = networks.sort((a, b) => a.name.localeCompare(b.name));
      dispatch(setNetworkItems(sortAlphabetically));
    } catch (e) {
      console.log(e);
    }
    dispatch(setNetworkOptionsLoading(false));
  };
};

export const loadClubsByClubName = (networkName: string) => {
  return async (dispatch: Dispatch<any>, getState: RootState) => {
    dispatch(setClubOptionsLoading(true));
    try {
      const clubs = await cachedGetClubsByClubName(networkName);
      const sortAlphabetically = clubs.sort((a, b) => a.name.localeCompare(b.name));
      dispatch(setClubItems(sortAlphabetically));
    } catch (e) {
      console.log(e);
    }
    dispatch(setClubOptionsLoading(false));
  };
};

export const register = () => {
  return async (dispatch: Dispatch<any>, getState: () => RootState): Promise<any> => {
    const state: State = getState().registration;
    const personalDetailsForm = state.personalDetails.form;
    const registerFirmDetailsForm = state.registerFirm.form;
    const dueDiligenceDetailsForm = state.dueDiligence.form;

    dispatch(setRegisterLoading(true));
    if (state.context.firm !== null) {
      try {
        return await registerAccountToExistingFirm(
          registerAccountToExistingFirmMapper(state.context.firm.id, personalDetailsForm),
        );
      } catch (e) {
        throw e;
      } finally {
        dispatch(setRegisterLoading(false));
      }
    } else {
      try {
        return await registerFirmWithManager(
          registerFirmWithManagerMapper(registerFirmDetailsForm, dueDiligenceDetailsForm, personalDetailsForm),
        );
      } catch (e) {
        throw e;
      } finally {
        dispatch(setRegisterLoading(false));
      }
    }
  };
};
